import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// EmailIntake — enterprise customers email work orders; the AI reads them and
// dispatches (2026-08-29). Three jobs on this page:
//   1. Show the vendor's inbound address (and provision it on first use).
//   2. Manage the trusted-sender domains that dispatch automatically.
//   3. Work the quarantine queue — everything untrusted or unclear waits here.

const MAIL_DOMAIN = 'in.giraops.com';

export default function EmailIntake() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [vendor, setVendor] = useState(null);
  const [skills, setSkills] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(doc(db, 'vendors', vendorId), (s) => setVendor(s.data() || {}));
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(collection(db, 'vendors', vendorId, 'skillCategories'),
      (s) => setSkills(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => {});
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(
        collection(db, 'vendors', vendorId, 'emailQuarantine'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
      ),
      (s) => setPending(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
  }, [vendorId]);

  const cfg = vendor?.emailIntake || {};
  const address = vendor?.emailIntakeAddress ? `${vendor.emailIntakeAddress}@${MAIL_DOMAIN}` : null;

  async function patch(next) {
    setBusy(true); setError(null);
    try {
      await httpsCallable(functions, 'updateVendorSettings')({
        patch: { emailIntake: { ...cfg, ...next } },
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function provision() {
    setBusy(true); setError(null);
    try { await httpsCallable(functions, 'provisionEmailIntake')({}); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  function addDomain(e) {
    e.preventDefault();
    const d = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!d || !d.includes('.')) return;
    const next = Array.from(new Set([...(cfg.trustedDomains || []), d]));
    setDomainInput('');
    patch({ trustedDomains: next });
  }

  return (
    <>
      <h2 style={{ margin: 0 }}>Email intake</h2>
      <p style={{ color: 'var(--muted)', marginTop: 4, maxWidth: 780 }}>
        Big customers' systems email work orders instead of using an app. Point them at the
        address below and Giraops reads each email — work-order number, priority, site, and
        which of your teams should handle it — then dispatches it automatically.
      </p>
      {error && <div className="error">{error}</div>}

      {/* ---- The address ---- */}
      <div className="card" style={{ padding: 18, marginTop: 8 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Your inbound address</h3>
        {address ? (
          <>
            <div style={{
              fontFamily: 'monospace', fontSize: 16, background: '#f8fafc',
              border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
            }}>
              <span>{address}</span>
              <button className="btn-chip" onClick={() => navigator.clipboard?.writeText(address)}>Copy</button>
            </div>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={cfg.enabled === true}
                disabled={busy}
                onChange={(e) => patch({ enabled: e.target.checked })}
                style={{ marginTop: 3, width: 16, height: 16 }}
              />
              <span style={{ fontSize: 14.5 }}>
                <strong>Accept work orders by email</strong>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                  While this is off, emails to that address are ignored.
                </div>
              </span>
            </label>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 0 }}>
              You don't have an inbound address yet.
            </p>
            <button className="btn-primary" onClick={provision} disabled={busy}>
              {busy ? 'Creating…' : 'Create my email address'}
            </button>
          </>
        )}
      </div>

      {/* ---- Trusted senders ---- */}
      <div className="card" style={{ padding: 18, marginTop: 14 }}>
        <h3 style={{ marginTop: 0, fontSize: 16 }}>Trusted sender domains</h3>
        <p style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 0 }}>
          Email from these domains dispatches automatically. Everything else waits below for
          you to approve — so a stray email can never wake your on-call technician.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {(cfg.trustedDomains || []).map((d) => (
            <span key={d} className="file-chip">
              @{d}
              <button
                type="button"
                onClick={() => patch({ trustedDomains: cfg.trustedDomains.filter((x) => x !== d) })}
              >x</button>
            </span>
          ))}
          {(cfg.trustedDomains || []).length === 0 && (
            <span style={{ fontSize: 13.5, color: 'var(--muted)' }}>None yet — everything will be held for review.</span>
          )}
        </div>
        <form onSubmit={addDomain} style={{ display: 'flex', gap: 8 }}>
          <input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="westfield.com.au"
            style={{ flex: 1, maxWidth: 320 }}
          />
          <button type="submit" className="btn-ghost" disabled={busy}>Add domain</button>
        </form>

        <label style={{ display: 'block', marginTop: 14, fontSize: 13.5 }}>
          If the AI can't tell which team should handle it, send it to:
        </label>
        <select
          value={cfg.defaultSkillCategoryId || ''}
          disabled={busy}
          onChange={(e) => patch({ defaultSkillCategoryId: e.target.value || null })}
          style={{ marginTop: 4, maxWidth: 320 }}
        >
          <option value="">Hold it for review instead</option>
          {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* ---- Quarantine ---- */}
      <h3 style={{ marginTop: 26, marginBottom: 6 }}>
        Needs review {pending?.length ? `(${pending.length})` : ''}
      </h3>
      {pending === null && <div className="placeholder">Loading…</div>}
      {pending && pending.length === 0 && (
        <div className="placeholder">Nothing waiting. Emails from trusted domains dispatch straight through.</div>
      )}
      {pending && pending.map((q) => (
        <QuarantineCard key={q.id} q={q} skills={skills} />
      ))}
    </>
  );
}

function QuarantineCard({ q, skills }) {
  const ex = q.extracted || {};
  const [skillId, setSkillId] = useState(ex.skillCategoryId || '');
  const [priority, setPriority] = useState(ex.priority || 'normal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  async function act(fn, payload) {
    setBusy(true); setErr(null);
    try { await httpsCallable(functions, fn)(payload); }
    catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <strong>{q.subject || '(no subject)'}</strong>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            from {q.from} · {q.createdAt?.toDate?.().toLocaleString() || ''}
          </div>
        </div>
        <span className="badge" style={{
          background: 'transparent', border: '1px solid #f59e0b', color: '#b45309', height: 'fit-content',
        }}>
          {q.reason}
        </span>
      </div>

      {ex.issueSummary && (
        <div style={{ marginTop: 10, fontSize: 14.5 }}>
          <strong>AI read it as:</strong> {ex.issueSummary}
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            {ex.woReference && <>WO {ex.woReference} · </>}
            priority {ex.priority}
            {ex.siteName && <> · {ex.siteName}</>}
            {ex.address && <> · {ex.address}</>}
          </div>
        </div>
      )}

      <button className="btn-chip" style={{ marginTop: 10 }} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} original email
      </button>
      {open && (
        <pre style={{
          whiteSpace: 'pre-wrap', fontSize: 12.5, background: '#f8fafc', padding: 10,
          borderRadius: 6, marginTop: 8, maxHeight: 260, overflow: 'auto',
        }}>{q.bodyPreview}</pre>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={skillId} onChange={(e) => setSkillId(e.target.value)} style={{ minWidth: 160 }}>
          <option value="">Choose team…</option>
          {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <button
          className="btn-primary"
          disabled={busy || !skillId}
          onClick={() => act('approveQuarantinedEmail', { quarantineId: q.id, skillCategoryId: skillId, priority })}
        >
          {busy ? 'Working…' : 'Approve + dispatch'}
        </button>
        <button
          className="btn-ghost-danger"
          disabled={busy}
          onClick={() => act('dismissQuarantinedEmail', { quarantineId: q.id })}
        >
          Dismiss
        </button>
      </div>
      {err && <div className="error">{err}</div>}
    </div>
  );
}
